import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchStudentInAppNotifications,
  markNotificationsRead,
  type StudentInAppNotification,
} from '../../utils/studentInAppNotifications';
import './StudentNotifications.css';

const StudentNotifications: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<StudentInAppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = await fetchStudentInAppNotifications();
        if (cancelled) return;
        setItems(list);
        markNotificationsRead(list.map((n) => n.id));
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load notifications');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="student-notifications-page">
      <button type="button" className="student-notifications-back" onClick={() => navigate('/dashboard')}>
        ← Back to dashboard
      </button>
      <h1 className="student-notifications-title">Notifications</h1>
      <p className="student-notifications-sub">
        Updates on your applications, documents, and payments.
      </p>

      {loading && (
        <div className="student-notifications-card loading">Loading…</div>
      )}
      {error && (
        <div className="student-notifications-card error">{error}</div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="student-notifications-card empty">
          <span className="empty-icon" aria-hidden>✓</span>
          <p>You're all caught up. No notifications right now.</p>
        </div>
      )}
      {!loading && !error && items.length > 0 && (
        <ul className="student-notifications-list">
          {items.map((n) => (
            <li
              key={n.id}
              className={`student-notifications-item tone-${n.tone}`}
            >
              <div className="item-icon" aria-hidden>
                {n.kind === 'application_feedback' && (n.tone === 'danger' ? '✕' : 'ℹ')}
                {n.kind === 'application_pending' && '⏱'}
                {n.kind === 'payment_due' && '💳'}
              </div>
              <div className="item-body">
                <div className="item-title-row">
                  <h2 className="item-title">{n.title}</h2>
                  {n.loanNumber && (
                    <span className="item-ref">#{n.loanNumber}</span>
                  )}
                </div>
                <p className="item-text">{n.body}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!loading && items.some((i) => i.kind === 'application_feedback') && (
        <div className="student-notifications-cta">
          <button type="button" className="cta-btn" onClick={() => navigate('/apply')}>
            Start new application
          </button>
        </div>
      )}
    </div>
  );
};

export default StudentNotifications;
